export const MAX_BROWSER_EXPORT_PROOF_BYTES = 250 * 1024 * 1024;

const SHA256_HEX = /^[a-f0-9]{64}$/i;

export interface ServerExportProofDescriptor {
  downloadUrl: string;
  diagnosticsUrl?: string;
  filename?: string;
  expiresAt?: string;
  sha256: string;
  sizeBytes: number;
}

export interface VerifiedExportProof {
  bytes: ArrayBuffer;
  sha256: string;
  sizeBytes: number;
  contentType: 'application/pdf';
}

const exportProofError = (message: string) => new Error(message);

export const validateServerExportProofDescriptor = (
  value: unknown
): ServerExportProofDescriptor => {
  const proof = value as Partial<ServerExportProofDescriptor> | null;
  if (!proof || typeof proof.downloadUrl !== 'string' || !proof.downloadUrl.trim()) {
    throw exportProofError('The export server did not return a proof artifact.');
  }
  if (typeof proof.sha256 !== 'string' || !SHA256_HEX.test(proof.sha256)) {
    throw exportProofError('The export server did not return a valid PDF SHA-256 identity.');
  }
  if (!Number.isSafeInteger(proof.sizeBytes)
    || Number(proof.sizeBytes) < 5
    || Number(proof.sizeBytes) > MAX_BROWSER_EXPORT_PROOF_BYTES) {
    throw exportProofError('The PDF proof size is missing or exceeds the browser safety limit.');
  }
  return {
    ...proof,
    downloadUrl: proof.downloadUrl.trim(),
    sha256: proof.sha256.toLowerCase(),
    sizeBytes: Number(proof.sizeBytes)
  } as ServerExportProofDescriptor;
};

const declaredContentLength = (headers: Headers): number | null => {
  const value = headers.get('content-length');
  if (value === null || value.trim() === '') return null;
  if (!/^\d+$/.test(value.trim())) {
    throw exportProofError('The proof artifact returned an invalid Content-Length header.');
  }
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    throw exportProofError('The proof artifact returned an invalid Content-Length header.');
  }
  return parsed;
};

const sha256 = async (bytes: ArrayBuffer): Promise<string> => {
  if (!window.crypto?.subtle) {
    throw exportProofError('This browser cannot verify the PDF proof identity securely.');
  }
  const digest = await window.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest))
    .map(value => value.toString(16).padStart(2, '0'))
    .join('');
};

const cancelReader = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
  try {
    await reader.cancel();
  } catch {
    // The browser may have already canceled the stream because its signal fired.
  }
};

const readBoundedResponse = async (
  response: Response,
  descriptor: ServerExportProofDescriptor,
  contentLength: number | null,
  signal: AbortSignal
): Promise<ArrayBuffer> => {
  const reader = response.body?.getReader();
  if (!reader) {
    // Older browsers without response streams may use arrayBuffer only when
    // the server declared the exact already-bounded byte count up front.
    if (contentLength === null || contentLength !== descriptor.sizeBytes) {
      throw exportProofError('This browser cannot safely verify a PDF without its exact Content-Length.');
    }
    const bytes = await response.arrayBuffer();
    if (bytes.byteLength > MAX_BROWSER_EXPORT_PROOF_BYTES
      || bytes.byteLength !== descriptor.sizeBytes) {
      throw exportProofError('The proof artifact size does not match the server diagnostic report.');
    }
    return bytes;
  }

  const combined = new Uint8Array(descriptor.sizeBytes);
  let receivedBytes = 0;
  while (true) {
    if (signal.aborted) {
      await cancelReader(reader);
      throw new DOMException('The PDF proof download was canceled.', 'AbortError');
    }
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    const nextReceivedBytes = receivedBytes + value.byteLength;
    if (nextReceivedBytes > descriptor.sizeBytes
      || nextReceivedBytes > MAX_BROWSER_EXPORT_PROOF_BYTES) {
      await cancelReader(reader);
      throw exportProofError('The proof artifact exceeded its authorized browser size.');
    }
    combined.set(value, receivedBytes);
    receivedBytes = nextReceivedBytes;
  }
  if (receivedBytes !== descriptor.sizeBytes) {
    throw exportProofError('The proof artifact size does not match the server diagnostic report.');
  }
  return combined.buffer;
};

export const fetchVerifiedExportProof = async (
  descriptorValue: unknown,
  signal: AbortSignal,
  fetchImplementation: typeof window.fetch = window.fetch.bind(window)
): Promise<VerifiedExportProof> => {
  const descriptor = validateServerExportProofDescriptor(descriptorValue);
  const response = await fetchImplementation(descriptor.downloadUrl, {
    method: 'GET',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    signal
  });
  const contentType = response.headers.get('content-type')
    ?.split(';')[0]
    .trim()
    .toLowerCase();
  if (!response.ok || contentType !== 'application/pdf') {
    throw exportProofError('The proof artifact could not be verified as a PDF.');
  }

  const contentLength = declaredContentLength(response.headers);
  if (contentLength !== null
    && (contentLength > MAX_BROWSER_EXPORT_PROOF_BYTES
      || contentLength !== descriptor.sizeBytes)) {
    throw exportProofError('The proof artifact size does not match the server diagnostic report.');
  }

  const bytes = await readBoundedResponse(response, descriptor, contentLength, signal);
  const signature = bytes.byteLength >= 5 ? new Uint8Array(bytes, 0, 5) : null;
  if (!signature
    || signature[0] !== 0x25
    || signature[1] !== 0x50
    || signature[2] !== 0x44
    || signature[3] !== 0x46
    || signature[4] !== 0x2d) {
    throw exportProofError('The proof artifact has an invalid PDF signature.');
  }

  const artifactHash = await sha256(bytes);
  if (artifactHash !== descriptor.sha256) {
    throw exportProofError('The proof artifact identity does not match the server diagnostic report.');
  }
  return {
    bytes,
    sha256: artifactHash,
    sizeBytes: bytes.byteLength,
    contentType: 'application/pdf'
  };
};
